import { format } from '../lib/date';
import { zeroPad } from '../lib/utils';
import { Period } from './Reporting';

const firstDayOfWeekIndex = 1; // Monday

// Martin Fowler's time point https://martinfowler.com/eaaDev/TimePoint.html
// #CAVEAT: Methods, excepts setters, are non-mutating.
export class MfDate {
  private _month: number; // Calendar month, 1-12
  private _year: number; // Calendar year, e.g. 2023
  private _day: number; // Calendar day, 1-31
  private _hour: number; // Wall clock hour, 0-23

  constructor(year: number, month: number, day: number, hour?: number) {
    const date = new Date(Date.UTC(year, month - 1, day, hour ?? 0));

    this._year = date.getUTCFullYear();
    this._month = date.getUTCMonth() + 1; // Months are 0-11 in JS Date, so we add 1
    this._day = date.getUTCDate();
    this._hour = date.getUTCHours();
  }

  clone() {
    return new MfDate(this._year, this._month, this._day, this._hour);
  }

  equals(date: MfDate): any {
    return (
      this._year === date.year &&
      this._month === date.month &&
      this._day === date.day &&
      this._hour === date.hour
    );
  }

  after(date: MfDate) {
    return this.getTimestampSeconds() > date.getTimestampSeconds();
  }

  afterOrEquals(date: MfDate): boolean {
    return this.getTimestampSeconds() >= date.getTimestampSeconds();
  }

  before(date: MfDate) {
    return this.getTimestampSeconds() < date.getTimestampSeconds();
  }

  get year(): number {
    return this._year;
  }
  set year(val: number) {
    this._year = val;
  }

  get month(): number {
    return this._month;
  }
  set month(val: number) {
    this._month = val;
  }

  get day(): number {
    return this._day;
  }
  set day(val: number) {
    this._day = val;
  }

  get hour(): number {
    return this._hour;
  }
  set hour(val: number) {
    this._hour = val;
  }

  addYears(years: number): MfDate {
    return new MfDate(this._year + years, this._month, this._day, this._hour);
  }

  addMonths(months: number): MfDate {
    return new MfDate(this._year, this._month + months, this._day, this._hour);
  }

  addDays(days: number): MfDate {
    return new MfDate(this._year, this._month, this._day + days, this._hour);
  }

  addHours(hours: number): MfDate {
    return new MfDate(this._year, this._month, this._day, this._hour + hours);
  }

  addPeriod(period: Period, i: number) {
    switch (period) {
      case Period.YEAR:
        return this.addYears(i);
      case Period.QUARTER:
        return this.addMonths(i * 3);
      case Period.MONTH:
        return this.addMonths(i);
      case Period.WEEK:
        return this.addDays(i * 7);
      case Period.DAY:
        return this.addDays(i);
      case Period.HOUR:
        return this.addHours(i);
      default:
        throw new Error(`Unsupported period ${period}`);
    }
  }

  subtract(seconds: number): MfDate {
    return MfDate.fromSeconds(this.getTimestampSeconds() - seconds);
  }

  lessDateInSeconds(date: MfDate) {
    const diff = this.getTimestampSeconds() - date.getTimestampSeconds();
    return diff;
  }

  truncate(period: Period) {
    switch (period) {
      case Period.YEAR:
        return this.truncateYear();
      case Period.QUARTER:
        return this.truncateQuarter();
      case Period.MONTH:
        return this.truncateMonth();
      case Period.WEEK:
        return this.truncateWeek();
      case Period.DAY:
        return this.truncateDay();
      case Period.HOUR:
        return this.truncateHour();
      default:
        throw new Error(`Unsupported period ${period}`);
    }
  }

  truncateYear() {
    return new MfDate(this._year, 1, 1, 0);
  }

  truncateQuarter() {
    const res = this.clone();
    const quarterStart = Math.floor((res.month - 1) / 3) * 3 + 1;
    res.month = quarterStart;
    res.day = 1;
    return res;
  }

  truncateMonth() {
    return new MfDate(this._year, this._month, 1, 0);
  }

  truncateDay() {
    return new MfDate(this._year, this._month, this._day, 0);
  }

  truncateHour() {
    return new MfDate(this._year, this._month, this._day, this._hour);
  }

  truncateWeek() {
    const weekDay = this.toDate().getUTCDay();
    const diff = weekDay >= firstDayOfWeekIndex ? weekDay - firstDayOfWeekIndex : 6 - weekDay;
    const date = this._day - diff;
    return new MfDate(this._year, this._month, date, 0);
  }

  truncateTime() {
    return new MfDate(this._year, this._month, this._day, 0);
  }

  yearEnd(): MfDate {
    const res = this.truncateYear();
    res.month = 12;
    res.day = 31;
    return res;
  }

  quarterEnd(): MfDate {
    const res = this.truncateQuarter();
    res.month += 3;
    res.day = 0;
    return res;
  }

  monthEnd(): MfDate {
    const res = this.truncateMonth();
    res.day = 0;
    res.month += 1;
    return res;
  }

  dayEnd(): MfDate {
    const res = this.truncateDay();
    return res;
  }

  hourEnd(): MfDate {
    const res = this.truncateHour();
    return res;
  }

  weekEnd(): MfDate {
    return this.truncateWeek().addDays(6);
  }

  endOf(period: Period) {
    switch (period) {
      case Period.YEAR:
        return this.yearEnd();
      case Period.QUARTER:
        return this.quarterEnd();
      case Period.MONTH:
        return this.monthEnd();
      case Period.WEEK:
        return this.weekEnd();
      case Period.DAY:
        return this.dayEnd();
      case Period.HOUR:
        return this.hourEnd();
      default:
        throw new Error(`Unsupported period ${period}`);
    }
  }

  isToday(): boolean {
    return MfDate.isToday(this);
  }

  toISOString(): string {
    return `${this._year}-${zeroPad(this._month, 2)}-${zeroPad(this._day, 2)}T${zeroPad(this._hour, 2)}:00:00Z`;
  }

  toString(): string {
    return `${this._year}-${zeroPad(this._month, 2)}-${zeroPad(this._day, 2)}`;
  }

  static now(): MfDate {
    const date = new Date();
    return new MfDate(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate(),
      date.getUTCHours(),
    );
  }

  static today(): MfDate {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    return new MfDate(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate(),
      date.getUTCHours(),
    );
  }

  static isToday(date: MfDate): boolean {
    const today = MfDate.today();
    return date.year === today.year && date.month === today.month && date.day === today.day;
  }

  static diffDays(start: MfDate, end: MfDate): number {
    const diffTime = Math.abs(end.lessDateInSeconds(start));
    return Math.ceil(diffTime / (24 * 60 * 60));
  }

  // @caveat: truncates to the hour
  static fromDate(date: Date): MfDate {
    return new MfDate(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate(),
      date.getUTCHours(),
    );
  }

  static fromDateParts(year: number, month: number, day: number, hour = 0): MfDate {
    return MfDate.fromDate(MfDate.dateFromDateParts(year, month, day, hour));
  }

  static fromString(dateString: string): MfDate {
    return MfDate.fromDate(new Date(dateString));
  }

  // #CAVEAT: truncates to the hour
  static fromSeconds(seconds: number): MfDate {
    const date = new Date(seconds * 1000);
    return MfDate.fromDate(date);
  }

  static fromTimestamp(milliseconds: number): MfDate {
    const date = new Date(milliseconds);
    return MfDate.fromDate(date);
  }

  // #NOTE: use with caution, it's returns local time
  toDate(): Date {
    return MfDate.dateFromDateParts(this._year, this._month, this._day, this._hour);
  }

  getTimestampSeconds(): number {
    const time_ms = this.toDate().getTime();
    return Math.floor(time_ms / 1000);
  }

  private static dateFromDateParts(year: number, month: number, day: number, hour: number): Date {
    return new Date(Date.UTC(year, month - 1, day, hour));
  }
}

// Martin Fowler's range https://martinfowler.com/eaaDev/Range.html
export class MfDateRange {
  private static DATE_PERIOD_SEPARATOR = ',';

  public start: MfDate;
  public end: MfDate;

  constructor(start: MfDate, end: MfDate) {
    this.start = start.clone();
    this.end = end.clone();
  }

  clone(): MfDateRange {
    return new MfDateRange(this.start.clone(), this.end.clone());
  }

  toString(): string {
    return this.toISOString();
  }

  toISOString(): string {
    return `${this.start.toISOString()}${MfDateRange.DATE_PERIOD_SEPARATOR}${this.end.toISOString()}`;
  }

  humanize(locale?: string): string {
    return `${format(this.start, undefined, locale)}–${format(this.end, undefined, locale)}`;
  }

  static fromStrings(start: string, end: string): MfDateRange {
    return new MfDateRange(MfDate.fromString(start), MfDate.fromString(end));
  }

  static fromString(arg: string): MfDateRange {
    const parts = arg.split(MfDateRange.DATE_PERIOD_SEPARATOR);
    if (parts.length !== 2) {
      throw new Error(`Invalid date range ${arg}`);
    }

    return MfDateRange.fromStrings(parts[0], parts[1]);
  }

  countPeriods(period: Period) {
    let i = 0;
    for (const _ of periods(this, period)) {
      i++;
    }

    return i;
  }

  isIntersecting(start: MfDate | undefined, end: MfDate | undefined) {
    return (
      (!start && !end) ||
      ((start ? start.before(this.end) : true) && (end ? end.afterOrEquals(this.start) : true))
    );
  }

  isInvalid() {
    return this.start.afterOrEquals(this.end);
  }

  static makeEndInclusive(range: MfDateRange): MfDateRange {
    const res = range.clone();
    res.end = res.end.addDays(1);

    return res;
  }

  static makeEndExclusive(range: MfDateRange): MfDateRange {
    if (range.isInvalid()) {
      return range;
    }

    const res = range.clone();
    res.end = res.end.addDays(-1);

    return res;
  }
}

// #TODO: Bring back the unit tests
export function* periods(range: MfDateRange, period: Period) {
  let i = 0;
  let date = range.start;
  while (date.before(range.end)) {
    yield date;

    i++;
    date = range.start.clone().truncate(period).addPeriod(period, i);
  }
}
